/**
 * SWISS School Portal - Cognito Hosted UI helpers
 * ================================================
 * Authorization Code grant with PKCE.
 * Flow:
 *   1. signInWithGoogle()       → window.location.assign(...)
 *   2. Cognito Hosted UI → Google → /auth/callback?code=...&state=...
 *   3. exchangeCodeForTokens()  → POST /oauth2/token
 *   4. cognitoLogout()          → redirect to Cognito /logout
 */

import { config } from '../config/config';

const base64UrlEncode = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomBytes = (length) => {
  const arr = new Uint8Array(length);
  window.crypto.getRandomValues(arr);
  return arr;
};

const generateCodeVerifier = () => base64UrlEncode(randomBytes(32));

const generateCodeChallenge = async (verifier) => {
  const data = new TextEncoder().encode(verifier);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(hash));
};

const cognitoOrigin = () => `https://${config.cognito.domain}`;

/**
 * Build the Cognito /oauth2/authorize URL and redirect the browser to it.
 * Passes identity_provider=Google so Cognito skips its own form and
 * goes straight to Google.
 */
async function buildAuthorizeUrl({ identityProvider } = {}) {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateCodeVerifier();

  sessionStorage.setItem(config.jwt.pkceVerifierKey, verifier);
  sessionStorage.setItem(config.jwt.oauthStateKey, state);

  const params = new URLSearchParams({
    client_id: config.cognito.userPoolClientId,
    response_type: 'code',
    scope: config.cognito.scopes.join(' '),
    redirect_uri: config.cognito.redirectSignIn,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  // identity_provider=Google makes Cognito skip its own form and bounce
  // straight to Google. Note: Cognito does NOT forward an OAuth `prompt`
  // (e.g. select_account) to Google — it strips it — so Google's account
  // chooser cannot be forced from here. A device already signed in to a
  // single Google account will federate it silently; the LoginPage hint
  // tells users to use a private window to pick a different account.
  if (identityProvider) params.set('identity_provider', identityProvider);

  return `${cognitoOrigin()}/oauth2/authorize?${params.toString()}`;
}

export const signInWithGoogle = async () => {
  const url = await buildAuthorizeUrl({ identityProvider: config.cognito.identityProvider });
  window.location.assign(url);
};

/**
 * Sign in with email + password. Used by school admins who have a
 * temp password from an SES invite — Cognito forces a password reset
 * on first sign-in.
 *
 * identity_provider=COGNITO pins the Hosted UI to the native
 * email/password form: the "Continue with Google" button is NOT
 * shown on that page. Without this, a school admin can land on the
 * Hosted UI, click Google, and federate a Google identity that has
 * no school_admin group — which the domain gate then rejects.
 */
export const signInWithEmail = async () => {
  const url = await buildAuthorizeUrl({ identityProvider: 'COGNITO' });
  window.location.assign(url);
};

/**
 * Exchange the authorization code for id/access/refresh tokens.
 * Returns the raw token payload from Cognito.
 */
export const exchangeCodeForTokens = async ({ code, state }) => {
  const verifier = sessionStorage.getItem(config.jwt.pkceVerifierKey);
  const expectedState = sessionStorage.getItem(config.jwt.oauthStateKey);

  if (!verifier) throw new Error('Missing PKCE verifier — start sign-in again.');
  if (state && expectedState && state !== expectedState) {
    throw new Error('OAuth state mismatch — possible CSRF, aborting.');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.cognito.userPoolClientId,
    code,
    redirect_uri: config.cognito.redirectSignIn,
    code_verifier: verifier,
  });

  const res = await fetch(`${cognitoOrigin()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  // One-shot use of PKCE artifacts
  sessionStorage.removeItem(config.jwt.pkceVerifierKey);
  sessionStorage.removeItem(config.jwt.oauthStateKey);

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Cognito token exchange failed (${res.status}): ${text || res.statusText}`);
  }

  return res.json();
};

/**
 * Programmatic email + password sign-in via Cognito's InitiateAuth
 * (USER_PASSWORD_AUTH flow). Used by the "Sign in as …" demo buttons
 * on the LoginPage — bypasses the Hosted UI redirect so demoing the
 * portal is one click.
 *
 * Cognito returns id/access/refresh tokens in the same shape the
 * OAuth code exchange returns, so the AuthContext consumes them the
 * same way.
 *
 * Requires the app client to have ALLOW_USER_PASSWORD_AUTH enabled.
 */
export const signInWithCredentials = async ({ email, password }) => {
  const body = {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: config.cognito.userPoolClientId,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  };
  const res = await fetch(`https://cognito-idp.${config.cognito.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Cognito sign-in failed (${res.status}): ${text || res.statusText}`);
  }
  const data = await res.json();
  if (!data?.AuthenticationResult) {
    throw new Error('Cognito did not return tokens (challenge required?).');
  }
  // Reshape so AuthContext's token persistence path is identical to
  // the OAuth code-exchange one.
  const r = data.AuthenticationResult;
  return {
    id_token: r.IdToken,
    access_token: r.AccessToken,
    refresh_token: r.RefreshToken,
    expires_in: r.ExpiresIn,
    token_type: r.TokenType || 'Bearer',
  };
};

/**
 * Refresh the id/access token using the stored refresh token.
 * Cognito public clients do not return a new refresh token here.
 */
export const refreshTokens = async (refreshToken) => {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.cognito.userPoolClientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(`${cognitoOrigin()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Cognito refresh failed (${res.status}): ${text || res.statusText}`);
  }

  return res.json();
};

/**
 * Redirect to Cognito's /logout endpoint so the federated session is cleared.
 */
export const cognitoLogout = () => {
  const params = new URLSearchParams({
    client_id: config.cognito.userPoolClientId,
    logout_uri: config.cognito.redirectSignOut,
  });
  window.location.assign(`${cognitoOrigin()}/logout?${params.toString()}`);
};
